import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { C, ROLE_NAMES, REQUEST_CATEGORIES, APPROVAL_ROUTES, getPerms, getUserRoles, getPrimaryRole, hasAnyRole, authIdOf } from "./constants";
import { getNotifications, markNotificationRead, createRequest, getAllUsers, createNotification, getEmployees } from "./supabaseClient";
import {
  authGetSession,
  authOnStateChange,
  authSignOut,
  linkAuthToUserRecord,
} from "./supabaseAuth";
import LoginPage, { ChangePasswordPage } from "./LoginPage";
import DashboardPage from "./DashboardPage";
import BoardPage from "./BoardPage";
import CommunitiesPage from "./CommunitiesPage";
import MosquesPage from "./MosquesPage";
import GrantsPage from "./GrantsPage";
import WomenPage from "./WomenPage";
import FinancePage from "./FinancePage";
import DonorsPage from "./DonorsPage";
import FundraisingPage from "./FundraisingPage";
import WaqfPage from "./WaqfPage";
import HRPage from "./HRPage";
import AssetsPage from "./AssetsPage";
import CommunicationsPage from "./CommunicationsPage";
import VolunteerPage from "./VolunteerPage";
import GovernancePage from "./GovernancePage";
import UsersPage from "./UsersPage";
import EDawahPage from "./EDawahPage";
import CampPage from "./CampPage";
import HousingPage from "./HousingPage";
import InstitutePage from "./InstitutePage";
import {
  LayoutDashboard, Landmark,
  Wallet, HeartHandshake, TrendingUp, Briefcase,
  Megaphone, HandHelping, Scale, UserCog,
  LogOut, Bell, BellDot, Search, Menu, X,
  Shield, Globe, Heart, Layers, CircleDot, ClipboardList, Building2, TreePine,
  Plus, Gavel, Users, Calendar, Mic, BookOpen, Star, Wrench, GraduationCap, Package,
  TrendingDown, Target, Gift, FileText, BarChart3, Handshake, Network,
  CalendarDays, Newspaper, Printer, Key, Clapperboard, Trophy, UserPlus, Zap,
  Clock, ScrollText, AlertTriangle, User, Lock, Inbox, Tent, Home
} from "lucide-react";
import { Modal, Field, Btn, useToast, RequestsTab } from "./erp-shared";

/* ═════════════════════════════════════════════
   ✦ Module Registry — كل موديول يتسجل هنا
   ═════════════════════════════════════════════ */
const MODULE_REGISTRY = {
  dashboard:       { key: "dashboard",       label: "لوحة التحكم",     icon: LayoutDashboard, color: C.teal },
  board:           { key: "board",           label: "مجلس الإدارة",     icon: Scale,           color: "#7c3aed" },
  communities:     { key: "communities",     label: "الجاليات",        icon: Globe,           color: "#2563eb" },
  mosques:         { key: "mosques",         label: "الدعوة",          icon: Landmark,        color: "#0891b2" },
  edawah:          { key: "edawah",          label: "الدعوة الإلكترونية", icon: Globe,         color: "#0ea5e9" },
  institute:       { key: "institute",       label: "معهد الثمر الداني", icon: GraduationCap,  color: "#a16207" },
  women:           { key: "women",           label: "القسم النسائي",   icon: Heart,           color: "#db2777" },
  housing:         { key: "housing",         label: "إسكان العاملات",  icon: Home,            color: "#be123c" },
  finance:         { key: "finance",         label: "المالية",         icon: Wallet,          color: "#16a34a" },
  donors:          { key: "donors",          label: "المتبرعين",       icon: HeartHandshake,  color: "#d97706" },
  fundraising:     { key: "fundraising",     label: "تنمية الموارد",   icon: TrendingUp,      color: "#7c3aed" },
  waqf:            { key: "waqf",            label: "الأوقاف والأملاك", icon: Landmark,        color: "#92400e" },
  hr:              { key: "hr",              label: "الموارد البشرية",  icon: Briefcase,       color: "#0284c7" },
  assets:          { key: "assets",          label: "الأصول والعُهد",   icon: Package,         color: "#d97706" },
  communications:  { key: "communications",  label: "الاتصال المؤسسي", icon: Megaphone,       color: "#c026d3" },
  volunteer:       { key: "volunteer",       label: "التطوع",          icon: HandHelping,     color: "#059669" },
  governance:      { key: "governance",      label: "الحوكمة",         icon: Shield,          color: "#6366f1" },
  grants:          { key: "grants",          label: "الجهات المانحة",  icon: Building2,       color: "#0284c7" },
  camp:            { key: "camp",            label: "مخيم فطروهم",     icon: Tent,            color: "#0d9488" },
  users:           { key: "users",           label: "المستخدمون",      icon: UserCog,         color: "#475569" },
};

/* ═════════════════════════════════════════════
   ✦ Placeholder for modules not yet built
   ═════════════════════════════════════════════ */
function PlaceholderModule({ moduleKey }) {
  const mod = MODULE_REGISTRY[moduleKey];
  const Icon = mod?.icon || Layers;
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", minHeight: "60vh", gap: 16, opacity: 0.5,
    }}>
      <Icon size={56} color={mod?.color || C.textLight} strokeWidth={1.2} />
      <h2 style={{ color: C.primary, fontWeight: 700, fontSize: 22, margin: 0 }}>
        {mod?.label || moduleKey}
      </h2>
      <p style={{ color: C.textLight, fontSize: 14, margin: 0 }}>
        سيتم بناء هذا الموديول قريباً...
      </p>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ MAIN APP
   ═════════════════════════════════════════════ */
export default function App() {
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState("dashboard");
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // Assign Task modal
  const [tasks, setTasks] = useState([]);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignEmployees, setAssignEmployees] = useState([]);
  const [showAssignTask, setShowAssignTask] = useState(false);
  const INITIAL_TASK = { title: "", description: "", assignee: "", department: "", priority: "عادي", deadline: "", status: "جديدة" };
  const [taskForm, setTaskForm] = useState(INITIAL_TASK);
  const [showAssignToast, AssignToastView] = useToast();
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);

  // Self-Service Request — available to ALL users
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showMyRequestsModal, setShowMyRequestsModal] = useState(false);
  const EMPTY_REQUEST = { category: "leave", type: "سنوية", department: "", date: "", reason: "", attachment: "", notes: "" };
  const [requestForm, setRequestForm] = useState(EMPTY_REQUEST);
  const requestNumber = useMemo(
    () => (showRequestModal ? `REQ-${Date.now().toString().slice(-6)}` : ""),
    [showRequestModal]
  );

  const notifRef = useRef(null);
  const userMenuRef = useRef(null);

  // ===== Supabase Auth: restore session + subscribe to state changes =====
  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await authGetSession();
      const sessionUser = data?.session?.user;
      if (!mounted || !sessionUser) return;
      const { data: linked } = await linkAuthToUserRecord(sessionUser);
      if (mounted && linked) setUser(linked);
    })();
    const sub = authOnStateChange(async (event, session) => {
      if (event === "SIGNED_OUT") {
        if (!mounted) return;
        setUser(null);
        setActivePage("dashboard");
        return;
      }
      if (event === "SIGNED_IN" && session?.user) {
        const { data: linked } = await linkAuthToUserRecord(session.user);
        if (mounted && linked) setUser(linked);
      }
    });
    return () => {
      mounted = false;
      try {
        sub?.data?.subscription?.unsubscribe?.();
      } catch (_e) {}
    };
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchResults(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Load notifications from Supabase
  useEffect(() => {
    if (!user) return;
    getNotifications(authIdOf(user))
      .then(n => setNotifications(Array.isArray(n) ? n : []))
      .catch(() => {});
  }, [user]);

  // Load real users for task assignment (need their ids to notify them)
  useEffect(() => {
    if (!user) return;
    getAllUsers()
      .then(list => { if (Array.isArray(list)) setAssignUsers(list.filter(u => u.is_active)); })
      .catch(() => {});
  }, [user]);

  // Load real employees to derive departments for task assignment
  useEffect(() => {
    if (!user) return;
    getEmployees()
      .then(list => { if (Array.isArray(list)) setAssignEmployees(list); })
      .catch(() => {});
  }, [user]);

  // Unique department names from live employees (replaces static ASSIGN_DEPARTMENTS)
  const assignDepartments = useMemo(
    () => [...new Set(assignEmployees.map(e => e.department).filter(Boolean))],
    [assignEmployees]
  );

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ===== Login / Password gate =====
  if (!user) {
    return <LoginPage onLogin={u => {
      setUser(u);
      showToast(`أهلاً ${u.name}!`);
    }} />;
  }

  if (user.must_change_password) {
    return <ChangePasswordPage user={user} onChanged={u => {
      setUser(u);
      showToast("تم تغيير كلمة المرور بنجاح");
    }} />;
  }

  // ===== Permissions — مدموجة من كل أدوار المستخدم (roles[]) =====
  const perms = getPerms(user);
  const userRoles = getUserRoles(user);
  const primaryRole = getPrimaryRole(user);
  // تكليف المهام: الأدوار الإدارية + أي مدير قسم أو مشرف
  const canAssignTasks =
    hasAnyRole(user, ["exec", "secretary", "system"]) ||
    userRoles.some(r => r.endsWith("_manager") || r.endsWith("_supervisor"));
  const allMenuItems = perms.modules.map(key => MODULE_REGISTRY[key]).filter(Boolean);
  const menuItems = searchQuery
    ? allMenuItems.filter(item => item.label.includes(searchQuery))
    : allMenuItems;

  // ===== Global Search Index =====
  const SEARCH_INDEX = [
    // Modules
    ...allMenuItems.map(m => ({ label: m.label, type: "موديول", icon: m.icon, page: m.key, tab: null })),
    // مجلس الإدارة
    { label: "أعضاء المجلس", type: "تاب", icon: Users, page: "board", tab: "members" },
    { label: "القرارات", type: "تاب", icon: Gavel, page: "board", tab: "decisions" },
    { label: "إضافة قرار", type: "إجراء", icon: Plus, page: "board", tab: "decisions" },
    { label: "الاجتماعات", type: "تاب", icon: Calendar, page: "board", tab: "meetings" },
    { label: "جدولة اجتماع", type: "إجراء", icon: Plus, page: "board", tab: "meetings" },
    { label: "اللجان", type: "تاب", icon: Building2, page: "board", tab: "committees" },
    { label: "إنشاء لجنة", type: "إجراء", icon: Plus, page: "board", tab: "committees" },
    // الجاليات
    { label: "الدعاة", type: "تاب", icon: Mic, page: "communities", tab: "preachers" },
    { label: "إضافة داعية", type: "إجراء", icon: Plus, page: "communities", tab: "preachers" },
    { label: "البرامج", type: "تاب", icon: BookOpen, page: "communities", tab: "programs" },
    { label: "إضافة برنامج", type: "إجراء", icon: Plus, page: "communities", tab: "programs" },
    { label: "المستفيدون", type: "تاب", icon: Users, page: "communities", tab: "beneficiaries" },
    { label: "إضافة مستفيد", type: "إجراء", icon: Plus, page: "communities", tab: "beneficiaries" },
    { label: "المسلمون الجدد", type: "تاب", icon: Star, page: "communities", tab: "newMuslims" },
    { label: "تسجيل مسلم جديد", type: "إجراء", icon: Plus, page: "communities", tab: "newMuslims" },
    // المساجد
    { label: "قائمة المساجد", type: "تاب", icon: Landmark, page: "mosques", tab: "mosques" },
    { label: "رعاية مسجد جديد", type: "إجراء", icon: Plus, page: "mosques", tab: "mosques" },
    { label: "إضافة مسجد", type: "إجراء", icon: Plus, page: "mosques", tab: "mosques" },
    { label: "الصيانة", type: "تاب", icon: Wrench, page: "mosques", tab: "maintenance" },
    { label: "طلب صيانة", type: "إجراء", icon: Plus, page: "mosques", tab: "maintenance" },
    { label: "الأئمة والمؤذنون", type: "تاب", icon: Mic, page: "mosques", tab: "imams" },
    { label: "إضافة إمام", type: "إجراء", icon: Plus, page: "mosques", tab: "imams" },
    // النسائي
    { label: "البرامج النسائية", type: "تاب", icon: BookOpen, page: "women", tab: "programs" },
    { label: "تأسيس برنامج نسائي", type: "إجراء", icon: Plus, page: "women", tab: "programs" },
    { label: "المستفيدات", type: "تاب", icon: Users, page: "women", tab: "beneficiaries" },
    { label: "إضافة مستفيدة", type: "إجراء", icon: Plus, page: "women", tab: "beneficiaries" },
    { label: "المعلمات", type: "تاب", icon: GraduationCap, page: "women", tab: "teachers" },
    { label: "إضافة معلمة", type: "إجراء", icon: Plus, page: "women", tab: "teachers" },
    { label: "الحلقات", type: "تاب", icon: CircleDot, page: "women", tab: "circles" },
    { label: "إعداد حلقة جديدة", type: "إجراء", icon: Plus, page: "women", tab: "circles" },
    // المالية
    { label: "الإيرادات", type: "تاب", icon: TrendingUp, page: "finance", tab: "income" },
    { label: "تسجيل إيراد", type: "إجراء", icon: Plus, page: "finance", tab: "income" },
    { label: "المصروفات", type: "تاب", icon: TrendingDown, page: "finance", tab: "expenses" },
    { label: "تسجيل مصروف", type: "إجراء", icon: Plus, page: "finance", tab: "expenses" },
    { label: "الميزانيات", type: "تاب", icon: Target, page: "finance", tab: "budgets" },
    { label: "الحسابات البنكية", type: "تاب", icon: Landmark, page: "finance", tab: "accounts" },
    // المتبرعين
    { label: "قائمة المتبرعين", type: "تاب", icon: HeartHandshake, page: "donors", tab: "donors" },
    { label: "إضافة متبرع", type: "إجراء", icon: Plus, page: "donors", tab: "donors" },
    { label: "التبرعات", type: "تاب", icon: Gift, page: "donors", tab: "donations" },
    { label: "تسجيل تبرع", type: "إجراء", icon: Plus, page: "donors", tab: "donations" },
    { label: "التعهدات", type: "تاب", icon: FileText, page: "donors", tab: "pledges" },
    { label: "التحليلات", type: "تاب", icon: BarChart3, page: "donors", tab: "analytics" },
    // تنمية الموارد
    { label: "المشاريع", type: "تاب", icon: TrendingUp, page: "fundraising", tab: "projects" },
    { label: "إنشاء مشروع", type: "إجراء", icon: Plus, page: "fundraising", tab: "projects" },
    { label: "الشراكات", type: "تاب", icon: Handshake, page: "fundraising", tab: "partnerships" },
    { label: "تأسيس شراكة", type: "إجراء", icon: Plus, page: "fundraising", tab: "partnerships" },
    // الموارد البشرية
    { label: "الموظفون", type: "تاب", icon: Users, page: "hr", tab: "employees" },
    { label: "إضافة موظف", type: "إجراء", icon: Plus, page: "hr", tab: "employees" },
    { label: "الهيكل التنظيمي", type: "تاب", icon: Network, page: "hr", tab: "structure" },
    { label: "الإجازات", type: "تاب", icon: CalendarDays, page: "hr", tab: "leaves" },
    { label: "طلب إجازة", type: "إجراء", icon: Plus, page: "hr", tab: "leaves" },
    { label: "الرواتب", type: "تاب", icon: Wallet, page: "hr", tab: "payroll" },
    // الاتصال المؤسسي
    { label: "الأخبار والمحتوى", type: "تاب", icon: Newspaper, page: "communications", tab: "news" },
    { label: "إنشاء خبر", type: "إجراء", icon: Plus, page: "communications", tab: "news" },
    { label: "المطبوعات", type: "تاب", icon: Printer, page: "communications", tab: "publications" },
    { label: "الموردين والتسويق", type: "تاب", icon: Building2, page: "communications", tab: "vendors" },
    { label: "اتفاق مورد جديد", type: "إجراء", icon: Plus, page: "communications", tab: "vendors" },
    { label: "حسابات التواصل", type: "تاب", icon: Key, page: "communications", tab: "socialAccounts" },
    { label: "إضافة حساب تواصل", type: "إجراء", icon: Plus, page: "communications", tab: "socialAccounts" },
    { label: "التغطيات الإعلامية", type: "تاب", icon: Clapperboard, page: "communications", tab: "media" },
    { label: "الإنجازات", type: "تاب", icon: Trophy, page: "communications", tab: "achievements" },
    // التطوع
    { label: "المتطوعون", type: "تاب", icon: UserPlus, page: "volunteer", tab: "volunteers" },
    { label: "إضافة متطوع", type: "إجراء", icon: Plus, page: "volunteer", tab: "volunteers" },
    { label: "الفرق التطوعية", type: "تاب", icon: Users, page: "volunteer", tab: "teams" },
    { label: "الفرص التطوعية", type: "تاب", icon: Zap, page: "volunteer", tab: "opportunities" },
    { label: "ساعات التطوع", type: "تاب", icon: Clock, page: "volunteer", tab: "hours" },
    // الحوكمة
    { label: "الخطة الاستراتيجية", type: "تاب", icon: Target, page: "governance", tab: "strategy" },
    { label: "اللوائح والسياسات", type: "تاب", icon: ScrollText, page: "governance", tab: "policies" },
    { label: "القوائم المالية", type: "تاب", icon: Wallet, page: "governance", tab: "financials" },
    { label: "درجة الحوكمة", type: "تاب", icon: Shield, page: "governance", tab: "compliance" },
    { label: "تضارب المصالح", type: "تاب", icon: AlertTriangle, page: "governance", tab: "conflicts" },
    { label: "تراخيص جمع التبرعات", type: "تاب", icon: FileText, page: "governance", tab: "licenses" },
    { label: "المخاطر", type: "تاب", icon: AlertTriangle, page: "governance", tab: "risks" },
    { label: "سجل التدقيق", type: "تاب", icon: Search, page: "governance", tab: "audit" },
    // المستخدمون
    { label: "إدارة المستخدمين", type: "تاب", icon: User, page: "users", tab: "users" },
    { label: "إضافة مستخدم", type: "إجراء", icon: Plus, page: "users", tab: "users" },
    { label: "الأدوار والصلاحيات", type: "تاب", icon: Shield, page: "users", tab: "roles" },
    { label: "سجل النظام", type: "تاب", icon: ClipboardList, page: "users", tab: "audit" },
    { label: "الجلسات النشطة", type: "تاب", icon: Lock, page: "users", tab: "sessions" },
    // عام
    { label: "الخطة التشغيلية", type: "تاب", icon: Target, page: "governance", tab: "operational" },
    { label: "التقارير", type: "تاب", icon: BarChart3, page: "governance", tab: "reports" },
    { label: "تكليف مهمة", type: "إجراء", icon: ClipboardList, page: null, tab: null },
  ];

  const searchResults = searchQuery.length >= 2
    ? SEARCH_INDEX.filter(item =>
        item.label.includes(searchQuery) &&
        (!item.page || perms.modules.includes(item.page) || item.page === "dashboard")
      ).slice(0, 10)
    : [];

  function handleSearchSelect(item) {
    if (item.label === "تكليف مهمة") {
      setShowAssignTask(true);
    } else if (item.page) {
      setActivePage(item.page);
    }
    setSearchQuery("");
    setShowSearchResults(false);
  }

  function handleSearchKey(e) {
    if (e.key !== "Enter" || !searchQuery) return;
    if (searchResults.length > 0) {
      handleSearchSelect(searchResults[0]);
    } else {
      showToast("لا توجد نتائج", "error");
    }
  }

  // ===== Render active module =====
  function renderModule() {
    // Each step replaces placeholder with real import:
    // case "dashboard": return <DashboardPage user={user} />;
    switch (activePage) {
      case "dashboard":
        return <DashboardPage user={user} onNavigate={page => setActivePage(page)} />;
      case "board":
        return <BoardPage user={user} />;
      case "communities":
        return <CommunitiesPage user={user} />;
      case "mosques":
        return <MosquesPage user={user} />;
      case "women":
        return <WomenPage user={user} />;
      case "housing":
        return <HousingPage user={user} perms={perms} />;
      case "finance":
        return <FinancePage user={user} />;
      case "donors":
        return <DonorsPage user={user} />;
      case "fundraising":
        return <FundraisingPage user={user} />;
      case "waqf":
        return <WaqfPage user={user} perms={perms} />;
      case "hr":
        return <HRPage user={user} />;
      case "assets":
        return <AssetsPage user={user} perms={perms} />;
      case "communications":
        return <CommunicationsPage user={user} />;
      case "volunteer":
        return <VolunteerPage user={user} />;
      case "governance":
        return <GovernancePage user={user} />;
      case "grants":
        return <GrantsPage user={user} />;
      case "users":
        return <UsersPage user={user} />;
      case "edawah":
        return <EDawahPage user={user} perms={perms} />;
      case "institute":
        return <InstitutePage user={user} perms={perms} />;
      case "camp":
        return <CampPage user={user} perms={perms} />;
      default:
        return <PlaceholderModule moduleKey={activePage} />;
    }
  }

  // ===== Notification actions =====
  async function handleNotifRead(notif) {
    try { await markNotificationRead(notif.id); } catch {}
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  async function handleLogout() {
    try {
      await authSignOut();
    } catch (_e) {}
    setUser(null);
    setActivePage("dashboard");
    setShowUserMenu(false);
  }

  async function handleAssignTask() {
    if (!taskForm.title || !taskForm.assignee) {
      showAssignToast("العنوان والموظف المكلّف مطلوبان", "error");
      return;
    }
    const assignedUser = assignUsers.find(u => u.id === taskForm.assignee);
    // احفظ الإشعار في Supabase أولاً — لا تُظهر نجاحًا إلا بعد نجاح الحفظ الفعلي
    try {
      await createNotification({
        // user_id لازم يكون auth.users.id — سياسة RLS تقارنه بـ auth.uid()
        user_id: authIdOf(assignedUser) || taskForm.assignee,
        title: taskForm.title,
        message: taskForm.description || `كلّفك ${user.name} بمهمة جديدة`,
        type: "task",
        related_id: null,
        created_by: authIdOf(user),
      });
    } catch (e) {
      showAssignToast(e?.message || "تعذّر حفظ التكليف", "error");
      return;
    }
    const newTask = {
      id: `tsk${Date.now()}`,
      ...taskForm,
      assigneeName: assignedUser?.name || "",
      assignedBy: user.name,
      assignedAt: new Date().toISOString().slice(0, 10),
    };
    setTasks(prev => [newTask, ...prev]);
    // لو المكلَّف هو المستخدم الحالي، حدّث جرس الإشعارات فورًا
    if (taskForm.assignee === user.id) {
      getNotifications(authIdOf(user))
        .then(n => setNotifications(Array.isArray(n) ? n : []))
        .catch(() => {});
    }
    setTaskForm(INITIAL_TASK);
    setShowAssignTask(false);
    showAssignToast("تم تكليف المهمة بنجاح");
  }

  return (
    <div style={styles.appContainer}>
      {/* ═══ Sidebar ═══ */}
      <aside className={`erp-sidebar${mobileMenuOpen ? " open" : ""}`} style={styles.sidebar}>
        {/* Header — تكليف مهمة */}
        <div style={styles.sidebarHeader}>
          {canAssignTasks && (
            <button
              onClick={() => setShowAssignTask(true)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", padding: "12px 16px", borderRadius: 12,
                background: `${C.accent}20`, border: `1.5px solid ${C.accent}50`,
                cursor: "pointer", fontFamily: "'Readex Pro', sans-serif",
                transition: "all 0.2s",
              }}
            >
              <ClipboardList size={22} color={C.accent} />
              <span style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>تكليف مهمة</span>
            </button>
          )}
          {!canAssignTasks && (
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>ERP v1.0</span>
          )}
        </div>

        {/* Nav items */}
        <nav style={styles.sidebarNav}>
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = activePage === item.key;
            return (
              <button
                key={item.key}
                onClick={() => { setActivePage(item.key); setMobileMenuOpen(false); }}
                style={{
                  ...styles.navItem,
                  background: isActive ? `${item.color}22` : "transparent",
                  color: isActive ? item.color : "rgba(255,255,255,0.7)",
                  fontWeight: isActive ? 700 : 500,
                  boxShadow: isActive ? `inset 3px 0 0 ${item.color}` : "none",
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                title={item.label}
              >
                <Icon size={18} />
                <span style={styles.navLabel}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={styles.sidebarFooter}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>ERP v1.0 — BNR</span>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && <div style={styles.mobileOverlay} onClick={() => setMobileMenuOpen(false)} />}

      {/* ═══ Main area ═══ */}
      <div className="erp-main" style={styles.mainArea}>
        {/* Header */}
        <header className="erp-header" style={styles.header}>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="erp-hamburger" style={styles.mobileMenuBtn}>
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Page title */}
          <div className="erp-page-title" style={styles.headerTitle}>
            {(() => {
              const mod = MODULE_REGISTRY[activePage];
              const Icon = mod?.icon || Layers;
              return (
                <>
                  <Icon size={22} color={mod?.color || C.teal} />
                  <span style={{ fontWeight: 700, color: C.primary, fontSize: 18 }}>{mod?.label}</span>
                </>
              );
            })()}
          </div>

          {/* Actions */}
          <div className="erp-actions" style={styles.headerActions}>
            {/* Search */}
            <div ref={searchRef} className="erp-search-box" style={{ ...styles.searchBox, position: "relative" }}>
              <Search size={16} color={C.textLight} />
              <input type="text" placeholder="بحث..." value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setShowSearchResults(true); }} onKeyDown={handleSearchKey} onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)} style={styles.searchInput} />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(""); setShowSearchResults(false); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", alignItems: "center" }} title="مسح">
                  <X size={14} color={C.textLight} />
                </button>
              )}
              {showSearchResults && searchResults.length > 0 && (
                <div style={styles.searchDropdown}>
                  {searchResults.map((item, i) => (
                    <button key={i} onClick={() => handleSearchSelect(item)} style={styles.searchResultItem}>
                      {item.icon && (() => { const Ic = item.icon; return <Ic size={16} color={C.textLight} style={{ width: 24 }} />; })()}
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, color: C.primary, fontSize: 13 }}>{item.label}</span>
                        <span style={{ fontSize: 11, color: C.textLight, marginRight: 8 }}>({item.type})</span>
                      </div>
                      {item.page && <span style={{ fontSize: 10, color: C.textLight, background: C.bg, padding: "2px 6px", borderRadius: 4 }}>{MODULE_REGISTRY[item.page]?.label}</span>}
                    </button>
                  ))}
                </div>
              )}
              {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div style={styles.searchDropdown}>
                  <div style={{ padding: 16, textAlign: "center", color: C.textLight, fontSize: 13 }}>لا توجد نتائج لـ "{searchQuery}"</div>
                </div>
              )}
            </div>

            {/* New Request Button — Self-Service */}
            <button
              onClick={() => {
                setRequestForm({
                  ...EMPTY_REQUEST,
                  department: user.department || "",
                  date: new Date().toISOString().slice(0, 10),
                });
                setShowRequestModal(true);
              }}
              title="طلب جديد / الخدمات الذاتية"
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 14px", borderRadius: 10,
                background: "#05966915", border: "1.5px solid #05966930",
                color: "#059669", fontWeight: 600, fontSize: 13,
                cursor: "pointer", fontFamily: "'Readex Pro', sans-serif",
                whiteSpace: "nowrap",
              }}
            >
              <TreePine size={16} />
              <span className="erp-leave-text">طلب جديد</span>
            </button>

            {/* My Requests Button */}
            <button
              onClick={() => setShowMyRequestsModal(true)}
              title="طلباتي — متابعة الحالة والمسار"
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 14px", borderRadius: 10,
                background: `${C.teal}15`, border: `1.5px solid ${C.teal}30`,
                color: C.teal, fontWeight: 600, fontSize: 13,
                cursor: "pointer", fontFamily: "'Readex Pro', sans-serif",
                whiteSpace: "nowrap",
              }}
            >
              <Inbox size={16} />
              <span className="erp-leave-text">طلباتي</span>
            </button>

            {/* Notifications */}
            <div ref={notifRef} style={{ position: "relative" }}>
              <button onClick={() => { setShowNotifs(!showNotifs); setShowUserMenu(false); }} style={styles.iconBtn} title="الإشعارات">
                {unreadCount > 0 ? <BellDot size={20} color={C.accent} /> : <Bell size={20} color={C.textLight} />}
                {unreadCount > 0 && <span style={styles.notifBadge}>{unreadCount}</span>}
              </button>
              {showNotifs && (
                <div className="erp-dropdown" style={styles.dropdown}>
                  <div style={styles.dropdownHeader}>
                    <span style={{ fontWeight: 700, color: C.primary }}>الإشعارات</span>
                    <span style={{ fontSize: 12, color: C.textLight }}>{unreadCount} غير مقروءة</span>
                  </div>
                  <div style={styles.dropdownBody}>
                    {notifications.length === 0 ? (
                      <p style={{ textAlign: "center", color: C.textLight, fontSize: 13, padding: 20 }}>لا توجد إشعارات</p>
                    ) : (
                      notifications.slice(0, 8).map(n => (
                        <div key={n.id} onClick={() => handleNotifRead(n)} style={{
                          ...styles.notifItem,
                          background: n.is_read ? "transparent" : `${C.teal}08`,
                          borderRight: n.is_read ? "3px solid transparent" : `3px solid ${C.teal}`,
                        }}>
                          <div style={{
                            width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0,
                            background: n.type === "success" ? C.success : n.type === "warning" ? C.warning : n.type === "error" ? C.danger : C.teal,
                          }} />
                          <div style={{ flex: 1 }}>
                            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.primary }}>{n.title}</p>
                            <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{n.message}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User */}
            <div ref={userMenuRef} style={{ position: "relative" }}>
              <button onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifs(false); }} style={styles.userBtn}>
                <div style={styles.avatar}>{user.name?.charAt(0)}</div>
                <div className="erp-user-info" style={styles.userInfo}>
                  <span style={styles.userName}>{user.name}</span>
                  <span style={styles.userRole}>
                    {ROLE_NAMES[primaryRole] || "—"}
                    {userRoles.length > 1 && ` +${userRoles.length - 1}`}
                  </span>
                </div>
              </button>
              {showUserMenu && (
                <div style={{ ...styles.dropdown, width: 240, left: 0 }}>
                  <div style={styles.dropdownBody}>
                    <div style={styles.userMenuHeader}>
                      <div style={styles.avatarLg}>{user.name?.charAt(0)}</div>
                      <div>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: C.primary }}>{user.name}</p>
                        <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{user.email}</p>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                          {userRoles.map(r => (
                            <span key={r} style={{
                              ...styles.roleBadge,
                              ...(r === primaryRole ? {} : { background: `${C.teal}15`, color: C.teal }),
                            }}>{ROLE_NAMES[r] || r}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div style={{ height: 1, background: C.border, margin: "0 12px" }} />
                    <button onClick={handleLogout} style={styles.menuItem}>
                      <LogOut size={16} color={C.danger} />
                      <span style={{ color: C.danger }}>تسجيل الخروج</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="erp-content" style={styles.content}>
          {renderModule()}
        </main>
      </div>

      {/* Assign Task toast */}
      {AssignToastView}

      {/* Assign Task Modal */}
      {showAssignTask && (
        <Modal title="تكليف مهمة" onClose={() => { setShowAssignTask(false); setTaskForm(INITIAL_TASK); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان المهمة" required placeholder="مثال: إعداد التقرير الشهري" value={taskForm.title} onChange={v => setTaskForm({ ...taskForm, title: v })} />
            <Field label="وصف المهمة" type="textarea" placeholder="تفاصيل المهمة والمطلوب..." rows={3} value={taskForm.description} onChange={v => setTaskForm({ ...taskForm, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الموظف المكلّف" type="select" options={assignUsers.map(u => ({ value: u.id, label: u.name }))} required value={taskForm.assignee} onChange={v => setTaskForm({ ...taskForm, assignee: v })} />
              <Field label="القسم" type="select" options={assignDepartments} value={taskForm.department} onChange={v => setTaskForm({ ...taskForm, department: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الأولوية" type="select" options={["عادي", "مرتفع", "عاجل"]} value={taskForm.priority} onChange={v => setTaskForm({ ...taskForm, priority: v })} />
              <Field label="الموعد النهائي" type="date" value={taskForm.deadline} onChange={v => setTaskForm({ ...taskForm, deadline: v })} />
              <Field label="الحالة" type="select" options={["جديدة", "جارية", "مكتملة"]} value={taskForm.status} onChange={v => setTaskForm({ ...taskForm, status: v })} />
            </div>
            <div style={{ padding: 10, background: `${C.teal}08`, borderRadius: 8, fontSize: 12, color: C.textLight }}>
              عدد المهام المكلّفة: <strong style={{ color: C.primary }}>{tasks.length}</strong>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={ClipboardList} onClick={handleAssignTask}>تكليف</Btn>
              <Btn variant="ghost" onClick={() => { setShowAssignTask(false); setTaskForm(INITIAL_TASK); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* My Requests Modal — scope=own */}
      {showMyRequestsModal && (
        <Modal title="طلباتي" onClose={() => setShowMyRequestsModal(false)} width={900}>
          <RequestsTab user={user} perms={perms} scope="own" />
        </Modal>
      )}

      {/* New Request Modal — Self-Service */}
      {showRequestModal && (() => {
        const currentCategory = REQUEST_CATEGORIES.find(c => c.key === requestForm.category) || REQUEST_CATEGORIES[0];
        const categoryLabels = REQUEST_CATEGORIES.map(c => c.label);
        return (
          <Modal title="طلب جديد" onClose={() => { setShowRequestModal(false); setRequestForm(EMPTY_REQUEST); }} width={580}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ padding: "12px 16px", background: `${C.primary}06`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <TreePine size={18} color="#059669" />
                  <div>
                    <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>مقدّم الطلب</p>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{user.name}</p>
                  </div>
                </div>
                <div style={{ textAlign: "left" }}>
                  <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>رقم الطلب</p>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.teal, fontFamily: "monospace" }}>{requestNumber}</p>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field
                  label="فئة الطلب"
                  type="select"
                  required
                  options={categoryLabels}
                  value={currentCategory.label}
                  onChange={v => {
                    const next = REQUEST_CATEGORIES.find(c => c.label === v) || REQUEST_CATEGORIES[0];
                    setRequestForm(p => ({ ...p, category: next.key, type: next.types[0] }));
                  }}
                />
                <Field
                  label="نوع الطلب"
                  type="select"
                  required
                  options={currentCategory.types}
                  value={requestForm.type}
                  onChange={v => setRequestForm(p => ({ ...p, type: v }))}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="الإدارة / القسم" value={requestForm.department} onChange={v => setRequestForm(p => ({ ...p, department: v }))} />
                <Field label="التاريخ" type="date" value={requestForm.date} onChange={v => setRequestForm(p => ({ ...p, date: v }))} />
              </div>
              <Field label="السبب" required type="textarea" rows={3} value={requestForm.reason} onChange={v => setRequestForm(p => ({ ...p, reason: v }))} />
              <Field label="مرفق (اسم الملف)" placeholder="اختياري" value={requestForm.attachment} onChange={v => setRequestForm(p => ({ ...p, attachment: v }))} />
              <Field label="ملاحظات" type="textarea" rows={2} value={requestForm.notes} onChange={v => setRequestForm(p => ({ ...p, notes: v }))} />
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <Btn icon={TreePine} onClick={async () => {
                  if (!requestForm.reason) { return; }
                  const nextStage = (APPROVAL_ROUTES[currentCategory.key] || [])[0] || "—";
                  const payload = {
                    req_number: requestNumber,
                    category: currentCategory.key,
                    type: requestForm.type,
                    applicant_id: user.id,
                    applicant_name: user.name,
                    department: requestForm.department || null,
                    request_date: requestForm.date || null,
                    reason: requestForm.reason,
                    attachment: requestForm.attachment || null,
                    notes: requestForm.notes || null,
                    created_by: user.id,
                  };
                  let savedRemote = false;
                  try {
                    await createRequest(payload);
                    savedRemote = true;
                  } catch (e) {
                    savedRemote = false;
                  }
                  setNotifications(prev => [{
                    id: `req-${Date.now()}`,
                    title: savedRemote ? "طلب جديد" : "طلب جديد (محلي)",
                    message: `${user.name} — ${currentCategory.label} — ${requestForm.type} → ${nextStage}`,
                    type: "warning",
                    is_read: false,
                  }, ...prev]);
                  setRequestForm(EMPTY_REQUEST);
                  setShowRequestModal(false);
                  setToast({ message: `تم إرسال الطلب رقم ${requestNumber}`, type: "success" });
                  setTimeout(() => setToast(null), 3000);
                }}>إرسال الطلب</Btn>
                <Btn variant="ghost" onClick={() => { setShowRequestModal(false); setRequestForm(EMPTY_REQUEST); }}>إلغاء</Btn>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "error" ? C.danger : C.success,
          color: C.white, padding: "12px 24px", borderRadius: 10, fontSize: 14,
          fontWeight: 600, fontFamily: "'Readex Pro', sans-serif",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)", zIndex: 9999,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {toast.type === "error" ? <X size={16} /> : <CircleDot size={16} />}
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }

        /* Desktop defaults */
        .erp-sidebar { width: 220px; }
        .erp-main { margin-right: 220px; }
        .erp-hamburger { display: none; }
        .erp-page-title { display: flex; }
        .erp-user-info { display: flex; }
        .erp-leave-text { display: inline; }
        .erp-content { padding: 20px; }

        @media (max-width: 768px) {
          .erp-sidebar {
            transform: translateX(100%);
            width: 260px !important;
            transition: transform 0.3s ease;
          }
          .erp-sidebar.open {
            transform: translateX(0) !important;
          }
          .erp-main {
            margin-right: 0 !important;
            width: 100vw !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
          }
          .erp-hamburger {
            display: flex !important;
          }
          .erp-page-title {
            display: none !important;
          }
          .erp-search-box {
            width: 100px !important;
            min-width: 80px !important;
          }
          .erp-leave-text {
            display: none !important;
          }
          .erp-user-info {
            display: none !important;
          }
          .erp-content {
            padding: 10px !important;
            overflow-x: hidden !important;
            max-width: 100vw !important;
          }
          .erp-header {
            padding: 0 8px !important;
            gap: 4px !important;
          }
          .erp-actions {
            gap: 4px !important;
          }
          .erp-dropdown {
            position: fixed !important;
            top: 60px !important;
            left: 8px !important;
            right: 8px !important;
            width: auto !important;
            max-height: 70vh !important;
            overflow-y: auto !important;
            z-index: 999 !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  appContainer: {
    display: "flex", minHeight: "100vh", direction: "rtl",
    fontFamily: "'Readex Pro', sans-serif", background: C.bg,
  },

  /* Sidebar */
  sidebar: {
    position: "fixed", top: 0, right: 0, bottom: 0,
    background: `linear-gradient(180deg, ${C.sidebar} 0%, ${C.primary} 100%)`,
    transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    zIndex: 100, display: "flex", flexDirection: "column", overflow: "hidden",
  },
  sidebarHeader: {
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)", minHeight: 56,
  },
  sidebarLogo: { display: "flex", alignItems: "center", gap: 10, overflow: "hidden" },
  sidebarLogoText: { display: "flex", flexDirection: "column", whiteSpace: "nowrap" },
  sidebarTitle: { color: C.white, fontWeight: 700, fontSize: 14 },
  sidebarSubtitle: { color: "rgba(255,255,255,0.4)", fontSize: 11 },
  collapseBtn: {
    background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 8,
    color: "rgba(255,255,255,0.6)", cursor: "pointer", padding: 8,
    display: "flex", alignItems: "center", transition: "all 0.2s",
  },
  sidebarNav: { flex: 1, overflowY: "auto", padding: "10px 8px", display: "flex", flexDirection: "column", gap: 3 },
  navItem: {
    display: "flex", alignItems: "center", gap: 10, width: "100%",
    padding: "9px 14px", border: "none", cursor: "pointer",
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 500,
    transition: "all 0.15s", whiteSpace: "nowrap", textAlign: "right",
    borderRadius: 8,
  },
  navLabel: { overflow: "hidden", textOverflow: "ellipsis" },
  sidebarFooter: {
    padding: 14, borderTop: "1px solid rgba(255,255,255,0.06)", textAlign: "center",
  },

  /* Mobile */
  mobileOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 },
  mobileMenuBtn: {
    background: "none", border: "none", color: C.primary, cursor: "pointer", padding: 4,
  },

  /* Main */
  mainArea: {
    flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh",
    transition: "margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },

  /* Header */
  header: {
    position: "sticky", top: 0, background: `${C.white}F0`,
    backdropFilter: "blur(12px)", borderBottom: `1px solid ${C.border}`,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0 20px", height: 60, zIndex: 50, gap: 12,
  },
  headerTitle: { alignItems: "center", gap: 10, flex: 1 },
  headerActions: { display: "flex", alignItems: "center", gap: 12 },

  /* Search */
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 10, padding: "9px 14px", border: `1px solid ${C.border}`, width: 280,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 14, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  searchDropdown: {
    position: "absolute", top: "calc(100% + 6px)", right: 0, left: 0,
    background: C.white, borderRadius: 12, boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
    border: `1px solid ${C.border}`, zIndex: 300, maxHeight: 400, overflowY: "auto",
  },
  searchResultItem: {
    display: "flex", alignItems: "center", gap: 10, width: "100%",
    padding: "10px 14px", border: "none", borderBottom: `1px solid ${C.bg}`,
    background: "none", cursor: "pointer", fontFamily: "'Readex Pro', sans-serif",
    textAlign: "right", transition: "background 0.15s",
  },

  /* Buttons */
  iconBtn: {
    background: "none", border: "none", cursor: "pointer", padding: 8,
    borderRadius: 8, display: "flex", alignItems: "center", position: "relative",
  },
  notifBadge: {
    position: "absolute", top: 2, left: 2, background: C.danger, color: C.white,
    fontSize: 10, fontWeight: 700, width: 18, height: 18, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${C.white}`,
  },

  /* Dropdowns */
  dropdown: {
    position: "absolute", top: "calc(100% + 10px)", left: 0, width: 360,
    background: C.white, borderRadius: 16, boxShadow: "0 12px 40px rgba(0,0,0,0.10)",
    border: `1px solid ${C.border}`, zIndex: 200, overflow: "hidden",
  },
  dropdownHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "14px 16px", borderBottom: `1px solid ${C.border}`, fontSize: 14,
  },
  dropdownBody: { maxHeight: 360, overflowY: "auto" },
  notifItem: {
    display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 16px",
    cursor: "pointer", transition: "background 0.15s", borderBottom: `1px solid ${C.bg}`,
  },

  /* User */
  userBtn: {
    display: "flex", alignItems: "center", gap: 10, background: "none",
    border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 10,
    fontFamily: "'Readex Pro', sans-serif",
  },
  avatar: {
    width: 36, height: 36, borderRadius: 10,
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 15,
  },
  userInfo: { flexDirection: "column", textAlign: "right" },
  userName: { fontSize: 13, fontWeight: 600, color: C.primary },
  userRole: { fontSize: 11, color: C.textLight },
  userMenuHeader: { display: "flex", alignItems: "center", gap: 12, padding: 16 },
  avatarLg: {
    width: 44, height: 44, borderRadius: 12,
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 18, flexShrink: 0,
  },
  roleBadge: {
    display: "inline-block", padding: "2px 8px", background: `${C.teal}15`,
    color: C.teal, borderRadius: 6, fontSize: 10, fontWeight: 600, marginTop: 4,
  },
  menuItem: {
    display: "flex", alignItems: "center", gap: 10, width: "100%",
    padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 500,
  },

  /* Content */
  content: { flex: 1 },
};
